import { formatApiErrorDetail } from "@/lib/api";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Camera, PaperPlaneTilt, Trash } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function AlarmAddendumModal({ landlords, prefill = {}, onClose, onCreated, addressOptions = [] }) {
  const [step, setStep] = useState("form"); // form | photos | ready
  const [emailsBlocked, setEmailsBlocked] = useState(false);
  const [form, setForm] = useState({
    address: prefill.address || "",
    unit: prefill.unit || "",
    landlord_id: prefill.landlord_id || "",
    owner_email: prefill.owner_email || "",
    owner_name: prefill.owner_name || "",
    reason: "",
    sensors: 8, cameras: 3,
    install_fee: 350.00, monthly_fee: 49.99,
  });
  const [saving, setSaving] = useState(false);
  const [addendum, setAddendum] = useState(null);
  const [photos, setPhotos] = useState([]);

  const token = () => localStorage.getItem("fr_token") || localStorage.getItem("token") || "";
  const auth = () => ({ headers: { Authorization: `Bearer ${token()}` } });

  // Check killswitch state so we don't accidentally send while site is under
  // setup. If ON we hide the "Send to Owner" button entirely.
  useEffect(() => {
    axios.get(`${BASE}/api/system/email-killswitch`, auth())
      .then(r => setEmailsBlocked(!!r.data?.enabled))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pickLandlord = (id) => {
    const l = landlords.find(x => x.id === id);
    setForm(f => ({
      ...f, landlord_id: id,
      owner_email: f.owner_email || l?.email || "",
      owner_name:  f.owner_name  || l?.name  || "",
    }));
  };

  const createDraft = async () => {
    if (!form.address.trim()) return toast.error("Address required");
    setSaving(true);
    try {
      const { data } = await axios.post(`${BASE}/api/alarm-addendums`, {
        address: form.address, unit: form.unit,
        landlord_id: form.landlord_id,
        owner_email: form.owner_email, owner_name: form.owner_name,
        reason: form.reason,
        scope: {
          sensors: Number(form.sensors) || 0,
          cameras: Number(form.cameras) || 0,
          install_fee_cents: Math.round(Number(form.install_fee || 0) * 100),
          monthly_fee_cents: Math.round(Number(form.monthly_fee || 0) * 100),
        },
      }, auth());
      setAddendum(data);
      setStep("photos");
      toast.success("Draft saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally { setSaving(false); }
  };

  const onPhotoPick = (e) => {
    const files = Array.from(e.target.files || []);
    setPhotos(prev => [...prev, ...files.map(f => ({ file: f, caption: "" }))].slice(0, 10));
    e.target.value = "";
  };

  const uploadPhotos = async () => {
    if (photos.length === 0) { setStep("ready"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      photos.forEach(p => { fd.append("files", p.file); fd.append("captions", p.caption || ""); });
      await axios.post(`${BASE}/api/alarm-addendums/${addendum.id}/evidence`, fd, {
        headers: { ...auth().headers, "Content-Type": "multipart/form-data" },
      });
      setStep("ready");
      toast.success(`${photos.length} photo${photos.length === 1 ? "" : "s"} attached`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Upload failed"));
    } finally { setSaving(false); }
  };

  const sendEmail = async () => {
    setSaving(true);
    try {
      await axios.post(`${BASE}/api/alarm-addendums/${addendum.id}/send`, {}, auth());
      toast.success("Email queued for owner");
      onCreated?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Send failed"));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-2 sm:p-6" data-testid="addendum-modal">
      <div className="max-w-2xl w-full bg-white border-2 border-[#0f172a] shadow-2xl max-h-[95vh] overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b-2 border-[#0f172a] flex items-center justify-between bg-amber-50">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-amber-800">
              {step === "form" ? "Step 1 · Scope" : step === "photos" ? "Step 2 · Evidence" : "Step 3 · Send"}
            </div>
            <h2 className="font-display font-black text-xl uppercase tracking-tight">Alarm Addendum</h2>
          </div>
          <button onClick={onClose} data-testid="addendum-close" className="h-10 w-10 flex items-center justify-center hover:bg-slate-200">
            <X size={20}/>
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {emailsBlocked && (
            <div className="p-3 border-2 border-red-600 bg-red-50" data-testid="addendum-kill-banner">
              <div className="text-[10px] font-black uppercase tracking-widest text-red-800">Setup Mode · Emails Blocked</div>
              <p className="text-xs text-slate-700 mt-1">
                Field Radar's email killswitch is ON — no owner emails will actually be sent from any draft you create here. Save the draft, mark it approved manually when the owner confirms out-of-band, and the Ring install Job will still be created.
              </p>
            </div>
          )}
          {step === "form" && (
            <>
              <Field label="Property Address" required>
                <input value={form.address} onChange={e => setForm({...form, address: e.target.value})}
                  list="addendum-address-list"
                  data-testid="addendum-address"
                  placeholder="Type or pick from your existing camera addresses"
                  className="w-full h-10 px-2 border-2 border-slate-300 text-sm font-mono"/>
                {addressOptions.length > 0 && (
                  <datalist id="addendum-address-list">
                    {addressOptions.map((a, i) => <option key={i} value={a}/>)}
                  </datalist>
                )}
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Unit">
                  <input value={form.unit} onChange={e => setForm({...form, unit: e.target.value})}
                    data-testid="addendum-unit"
                    className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
                </Field>
                <Field label="Landlord">
                  <select value={form.landlord_id} onChange={e => pickLandlord(e.target.value)}
                    data-testid="addendum-landlord"
                    className="w-full h-10 px-2 border-2 border-slate-300 text-sm">
                    <option value="">— Pick landlord —</option>
                    {landlords.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Owner Name">
                  <input value={form.owner_name} onChange={e => setForm({...form, owner_name: e.target.value})}
                    data-testid="addendum-owner-name"
                    className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
                </Field>
                <Field label="Owner Email" required>
                  <input type="email" value={form.owner_email} onChange={e => setForm({...form, owner_email: e.target.value})}
                    data-testid="addendum-owner-email"
                    className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
                </Field>
              </div>
              <Field label="Reason for install">
                <textarea value={form.reason} onChange={e => setForm({...form, reason: e.target.value})}
                  data-testid="addendum-reason" rows={3}
                  placeholder="e.g. Unit vacant 6+ months, neighbors report vagrant activity, prior break-in in 2025..."
                  className="w-full px-2 py-1 border-2 border-slate-300 text-sm"/>
              </Field>
              <div className="p-3 border-2 border-amber-500 bg-amber-50">
                <div className="text-[10px] font-black uppercase tracking-widest text-amber-800 mb-2">Scope & Pricing</div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <Field label="Sensors">
                    <input type="number" min="0" value={form.sensors} onChange={e => setForm({...form, sensors: e.target.value})}
                      data-testid="addendum-sensors"
                      className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
                  </Field>
                  <Field label="Cameras">
                    <input type="number" min="0" value={form.cameras} onChange={e => setForm({...form, cameras: e.target.value})}
                      data-testid="addendum-cameras"
                      className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Install Fee ($ one-time)">
                    <input type="number" min="0" step="0.01" value={form.install_fee} onChange={e => setForm({...form, install_fee: e.target.value})}
                      data-testid="addendum-install-fee"
                      placeholder="e.g. 350.00"
                      className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
                  </Field>
                  <Field label="Monthly Monitoring ($/mo)">
                    <input type="number" min="0" step="0.01" value={form.monthly_fee} onChange={e => setForm({...form, monthly_fee: e.target.value})}
                      data-testid="addendum-fee"
                      className="w-full h-10 px-2 border-2 border-slate-300 text-sm"/>
                  </Field>
                </div>
              </div>
            </>
          )}

          {step === "photos" && addendum && (
            <>
              <p className="text-sm text-slate-600">Attach photos showing why the alarm is needed. Broken locks, vacancy signs, prior damage — anything the owner should see. Max 10 photos.</p>
              <label className="block h-24 border-2 border-dashed border-slate-400 hover:border-amber-500 hover:bg-amber-50 cursor-pointer flex items-center justify-center gap-2 text-sm font-black uppercase tracking-widest text-slate-600"
                data-testid="addendum-photo-picker">
                <Camera size={22}/> Tap to add photos
                <input type="file" accept="image/*" multiple className="hidden" onChange={onPhotoPick}/>
              </label>
              {photos.length > 0 && (
                <div className="space-y-2">
                  {photos.map((p, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 border border-slate-300 bg-slate-50">
                      <div className="text-xs flex-1 truncate">{p.file.name}</div>
                      <input placeholder="Caption (optional)" value={p.caption}
                        onChange={e => setPhotos(prev => prev.map((x,idx) => idx===i ? {...x, caption: e.target.value} : x))}
                        className="flex-1 h-8 px-2 border border-slate-300 text-xs"/>
                      <button onClick={() => setPhotos(prev => prev.filter((_,idx) => idx !== i))}
                        className="h-8 w-8 flex items-center justify-center text-red-600 hover:bg-red-50">
                        <Trash size={14}/>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-slate-500">You can skip this step and send the addendum without photos.</p>
              {/* Live preview of the current draft — updates whenever you
                  come back to this step after adding evidence. */}
              <div className="mt-3 border-2 border-slate-300" data-testid="addendum-live-preview-photos">
                <div className="px-2 py-1 bg-slate-100 border-b border-slate-300 text-[10px] font-black uppercase tracking-widest text-slate-600 flex items-center justify-between">
                  <span>Live Preview · with your info + logo</span>
                  <a href={`${BASE}/api/alarm-addendums/${addendum.id}/pdf?token=${addendum.view_token || ""}`}
                     target="_blank" rel="noreferrer"
                     className="text-fuchsia-700 underline font-black"
                     data-testid="addendum-open-pdf-photos">
                    Open full-screen ↗
                  </a>
                </div>
                <iframe title="Addendum preview"
                  src={`${BASE}/api/alarm-addendums/${addendum.id}/pdf?token=${addendum.view_token || ""}#toolbar=0`}
                  className="w-full h-72 bg-white"/>
              </div>
            </>
          )}

          {step === "ready" && addendum && (
            <>
              <div className="p-4 border-2 border-emerald-500 bg-emerald-50">
                <div className="text-[10px] font-black uppercase tracking-widest text-emerald-800">Ready to Send</div>
                <div className="mt-2 text-sm text-slate-800">
                  Sending to <b>{addendum.owner_email}</b><br/>
                  {photos.length > 0 && <span className="text-xs text-slate-500">{photos.length} evidence photo{photos.length===1?"":"s"} attached</span>}
                </div>
              </div>
              <p className="text-xs text-slate-600">
                Owner will get an email with "Approve" and "Decline" buttons. When approved a Ring install Job is automatically created and dropped on the board with priority=high.
              </p>
              {/* Full-height inline preview so admin sees exactly what the
                  owner will get before firing the email. */}
              <div className="mt-2 border-2 border-slate-300" data-testid="addendum-live-preview-ready">
                <div className="px-2 py-1 bg-slate-100 border-b border-slate-300 text-[10px] font-black uppercase tracking-widest text-slate-600 flex items-center justify-between">
                  <span>Final Addendum · what the owner will see</span>
                  <a href={`${BASE}/api/alarm-addendums/${addendum.id}/pdf?token=${addendum.view_token || ""}`}
                     target="_blank" rel="noreferrer"
                     className="text-fuchsia-700 underline font-black"
                     data-testid="addendum-open-pdf-ready">
                    Open full-screen ↗
                  </a>
                </div>
                <iframe title="Addendum final preview"
                  src={`${BASE}/api/alarm-addendums/${addendum.id}/pdf?token=${addendum.view_token || ""}#toolbar=0`}
                  className="w-full h-96 bg-white"/>
              </div>
            </>
          )}
        </div>

        <div className="p-3 border-t-2 border-[#0f172a] flex gap-2 justify-end bg-slate-50">
          <button onClick={onClose} className="h-10 px-3 border-2 border-slate-400 font-black uppercase text-[11px] tracking-widest">Cancel</button>
          {step === "form" && (
            <button onClick={createDraft} disabled={saving} data-testid="addendum-save-draft"
              className="h-10 px-3 bg-[#0f172a] text-white font-black uppercase text-[11px] tracking-widest disabled:opacity-50">
              {saving ? "Saving…" : "Save Draft & Next"}
            </button>
          )}
          {step === "photos" && (
            <button onClick={uploadPhotos} disabled={saving} data-testid="addendum-upload-photos"
              className="h-10 px-3 bg-[#0f172a] text-white font-black uppercase text-[11px] tracking-widest disabled:opacity-50">
              {saving ? "Uploading…" : photos.length ? `Upload ${photos.length} & Next` : "Skip Photos & Next"}
            </button>
          )}
          {step === "ready" && (
            <button onClick={sendEmail} disabled={saving || emailsBlocked} data-testid="addendum-send"
              className="h-10 px-3 bg-emerald-600 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
              <PaperPlaneTilt size={14}/> {emailsBlocked ? "Send Disabled" : (saving ? "Sending…" : "Send to Owner")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <label className="block">
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">
        {label}{required && <span className="text-red-600 ml-0.5">*</span>}
      </div>
      {children}
    </label>
  );
}
